import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { UserContext } from "../../utils/contexts/UserContext";
import { canEditAllIn } from "../../utils/folders";
import { focusRing } from "../Exams/TestBuilder/ui";
import { questionBooklets } from "./review/bookletLock";

// Older backends send no `actions`: who edits a question that is not approved yet. In a folder's subject only the
// role there counts (moderator, expert and admins edit any, an author their own, a viewer none); outside folders
// the author edits their own, as the pencil in My questions.
function legacyMayEdit(question, own, folderRole) {
  if (question.status?.code === "APPROVED") return false;
  return question.folder ? canEditAllIn(folderRole) || (own && folderRole === "author") : own;
}

// Where the question is edited, if the user may edit it now: the detail's actions.edit says so (by role and
// stage: an author's question under review is locked), else the old role rule. A draft is its author's alone and
// opens in the draft editor; a question an active BTS booklet prints is edited by nobody. `folderRole` is the user's
// role in the question's subject, if any.
export function questionEditPath(question, user, folderRole = null) {
  if (!question?.uuid || !user?.uuid || questionBooklets(question).length) return null;
  const own = question.author?.uuid === user.uuid;
  const allowed = question.actions ? !!question.actions.edit : legacyMayEdit(question, own, folderRole);
  if (!allowed) return null;
  if (question.status?.code === "DRAFT") return own ? `/my-questions/draft/${question.uuid}` : null;
  return `/my-questions/edit/${question.uuid}`;
}

// The page's one main button (FolderBar's look): 44px on phones, where it fills its row next to "⋯".
const primaryButton = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 motion-safe:active:scale-[0.98] md:h-9 ${focusRing}`;

export default function QuestionEditButton({ question, folderRole = null, className = "" }) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const path = questionEditPath(question, user, folderRole);
  if (!path) return null;
  return (
    <button
      type="button"
      // The editor comes back to this page after saving.
      onClick={() => navigate(path, { state: { from: `/questions/${question.uuid}` } })}
      className={`${primaryButton} ${className}`}
    >
      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
      {t("buttons.edit")}
    </button>
  );
}
